/**
 * Utility functions for Google Analytics tracking
 */
import { useAnalytics } from '../contexts/AnalyticsContext';

/**
 * Event categories for GA4 tracking
 */
export enum EventCategory {
  NAVIGATION = 'navigation',
  INTERACTION = 'interaction',
  FILTER = 'filter',
  CHART = 'chart',
  SERVER = 'server',
  ERROR = 'error',
  PERFORMANCE = 'performance',
}

/**
 * Common events for GA4 tracking
 */
export const trackEvents = {
  /**
   * Track page view
   * @param path Page path
   * @param title Page title
   */
  pageView: (path: string, title?: string) => {
    const { trackPageView } = useAnalytics();
    trackPageView(path, title);
  },

  /**
   * Track server view
   * @param serverId Server ID
   * @param serverName Server name
   */
  serverView: (serverId: string, serverName: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('server_view', {
      category: EventCategory.SERVER,
      server_id: serverId,
      server_name: serverName,
    });
  },

  /**
   * Track server comparison
   * @param serverIds Array of server IDs
   * @param serverCount Number of servers being compared
   */
  serverComparison: (serverIds: string[], serverCount: number) => {
    const { trackEvent } = useAnalytics();
    trackEvent('server_comparison', {
      category: EventCategory.SERVER,
      server_ids: serverIds.join(','),
      server_count: serverCount,
    });
  },

  /**
   * Track chart interaction
   * @param chartName Chart name
   * @param action Action performed (e.g., 'zoom', 'filter', 'export')
   */
  chartInteraction: (chartName: string, action: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('chart_interaction', {
      category: EventCategory.CHART,
      chart_name: chartName,
      action: action,
    });
  },

  /**
   * Track filter application
   * @param filterType Type of filter
   * @param filterValue Value of filter
   */
  filterApplied: (filterType: string, filterValue: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('filter_applied', {
      category: EventCategory.FILTER,
      filter_type: filterType,
      filter_value: filterValue,
    });
  },

  /**
   * Track time range selection
   * @param timeRange Selected time range
   */
  timeRangeSelected: (timeRange: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('time_range_selected', {
      category: EventCategory.FILTER,
      time_range: timeRange,
    });
  },

  /**
   * Track button click
   * @param buttonName Button name
   * @param location Location where the button was clicked
   */
  buttonClick: (buttonName: string, location: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('button_click', {
      category: EventCategory.INTERACTION,
      button_name: buttonName,
      location: location,
    });
  },

  /**
   * Track error
   * @param errorType Type of error
   * @param errorMessage Error message
   * @param errorLocation Location where the error occurred
   */
  error: (errorType: string, errorMessage: string, errorLocation: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('error', {
      category: EventCategory.ERROR,
      error_type: errorType,
      error_message: errorMessage,
      error_location: errorLocation,
    });
  },

  /**
   * Track performance metric
   * @param metricName Name of the performance metric
   * @param value Value of the metric
   */
  performance: (metricName: string, value: number) => {
    const { trackEvent } = useAnalytics();
    trackEvent('performance', {
      category: EventCategory.PERFORMANCE,
      metric_name: metricName,
      value: value,
    });
  },
};
